import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import "../../styles/serviceCallStyles/ServiceCallDescription.css";

const ServiceCallDescription = forwardRef(({ data, setData }, ref) => {
  const [formData, setFormData] = useState(() => {
    const savedData = JSON.parse(localStorage.getItem("serviceData")) || {};
    return {
      serviceCallDescription:
        data?.serviceCallDescription || savedData.serviceCallDescription || "",
      address: data?.address || savedData.address || "",
      postCode: data?.postCode || savedData.postCode || "",
      firstName: data?.firstName || savedData.firstName || "",
      lastName: data?.lastName || savedData.lastName || "",
      contactPhone: data?.contactPhone || savedData.contactPhone || "",
      contactEmail: data?.contactEmail || savedData.contactEmail || "",
      siteStatus: data?.siteStatus || savedData.siteStatus || "Offsite",
    };
  });

  const [errors, setErrors] = useState({});
  const [isTouched, setIsTouched] = useState(false);

  // Update localStorage on formData change
  useEffect(() => {
    localStorage.setItem("serviceData", JSON.stringify(formData));
  }, [formData]);

  // Sync with localStorage if it changes (e.g., externally updated)
  useEffect(() => {
    const handleStorageChange = () => {
      const savedData = JSON.parse(localStorage.getItem("serviceData")) || {};
      setFormData((prev) => ({
        ...prev,
        ...savedData,
      }));
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const validateField = (key, value) => {
    let error = "";

    switch (key) {
      case "serviceCallDescription":
        error =
          value.trim() === "" ? "Service call description is required." : "";
        break;
      case "address":
        error = value.trim() === "" ? "Address is required." : "";
        break;
      case "postCode":
        error = value.trim() === "" ? "Postcode is required." : "";
        break;
      case "firstName":
        error = value.trim() === "" ? "First name is required." : "";
        break;
      case "lastName":
        error = value.trim() === "" ? "Last name is required." : "";
        break;
      case "contactPhone":
        const sanitizedValue = value.replace(/\s+/g, ""); // Remove all spaces
        if (sanitizedValue.trim() === "") {
          error = "Phone number is required.";
        } else if (!/^\d+$/.test(sanitizedValue)) {
          error = "Phone number should contain only numbers.";
        }
        break;
      case "contactEmail":
        if (value.trim() === "") {
          error = "Email is required.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          error = "Please enter a valid email address.";
        }
        break;
      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    let valid = true;

    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) valid = false;
      newErrors[key] = error;
    });

    setErrors(newErrors);
    return valid; // Return overall validation status
  };

  useImperativeHandle(ref, () => ({
    validateForm: () => {
      setIsTouched(true); // Mark form as touched
      return validateForm();
    },
  }));

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setData((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="servicecall-description-container">
      <div className="servicecallscreen">
        <div className="servicecall-header-container">
          <h3 className="form-title">Service Call Description</h3>
          <div className="servicecall-form-container">
            <form>
              <h3 className="row-title">House Details</h3>
              <div className="row">
                <div className="input-group">
                  <label htmlFor="address">Address</label>
                  <input
                    type="text"
                    id="address"
                    placeholder="Enter Address"
                    value={formData.address}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                  />
                  {isTouched && errors.address && (
                    <span className="error-text">{errors.address}</span>
                  )}
                </div>
                <div className="input-group">
                  <label htmlFor="postCode">Postal Code</label>
                  <input
                    type="text"
                    id="postCode"
                    placeholder="Enter Post Code"
                    value={formData.postCode}
                    onChange={(e) =>
                      handleInputChange("postCode", e.target.value)
                    }
                  />
                  {isTouched && errors.postCode && (
                    <span className="error-text">{errors.postCode}</span>
                  )}
                </div>
              </div>
              <hr />

              <h3 className="row-title">Contact Details</h3>
              <div className="row">
                <div className="input-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    placeholder="Enter First Name"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                  />
                  {isTouched && errors.firstName && (
                    <span className="error-text">{errors.firstName}</span>
                  )}
                </div>
                <div className="input-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    placeholder="Enter Last Name"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                  />
                  {isTouched && errors.lastName && (
                    <span className="error-text">{errors.lastName}</span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="input-group">
                  <label htmlFor="contactPhone">Phone Number</label>
                  <input
                    type="tel"
                    id="contactPhone"
                    placeholder="Enter Contact Phone"
                    value={formData.contactPhone}
                    onChange={(e) =>
                      handleInputChange("contactPhone", e.target.value)
                    }
                  />
                  {isTouched && errors.contactPhone && (
                    <span className="error-text">{errors.contactPhone}</span>
                  )}
                </div>
                <div className="input-group">
                  <label htmlFor="contactEmail">Email Address</label>
                  <input
                    type="email"
                    id="contactEmail"
                    placeholder="Enter Contact Email"
                    value={formData.contactEmail}
                    onChange={(e) =>
                      handleInputChange("contactEmail", e.target.value)
                    }
                    className={isTouched && errors.contactEmail ? "error" : ""}
                  />
                  {isTouched && errors.contactEmail && (
                    <span className="error-text">{errors.contactEmail}</span>
                  )}
                </div>
              </div>
              <hr />

              <div className="row">
                <div className="input-group full-width">
                  <label htmlFor="serviceCallDescription">
                    Service Call Overview
                  </label>

                  <textarea
                    id="serviceCallDescription"
                    placeholder="Enter service call description"
                    value={formData.serviceCallDescription}
                    onChange={(e) =>
                      handleInputChange(
                        "serviceCallDescription",
                        e.target.value
                      )
                    }
                  />

                  {isTouched && errors.serviceCallDescription && (
                    <span className="error-text">
                      {errors.serviceCallDescription}
                    </span>
                  )}
                </div>

                <div className="input-group">
                  <label htmlFor="siteStatus">Site Status</label>
                  <div className="radio-group">

                    <div className="radio-group-selection">
                      <label>
                        <input
                          type="radio"
                          name="siteStatus"
                          value="Onsite"
                          checked={formData.siteStatus === "Onsite"}
                          onChange={(e) =>
                            handleInputChange("siteStatus", e.target.value)
                          }
                        />
                        Onsite
                      </label>
                    </div>
                    <div className="radio-group-selection">
                      <label>
                        <input
                          type="radio"
                          name="siteStatus"
                          value="Offsite"
                          checked={formData.siteStatus === "Offsite"}
                          onChange={(e) =>
                            handleInputChange("siteStatus", e.target.value)
                          }
                        />
                        Offsite
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ServiceCallDescription;
